import type { DetailPreview } from '@/contexts/DetailPreviewContext';
import { useDetailPreview } from '@/contexts/DetailPreviewContext';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export interface UseListPageWithPreviewOptions<TItem extends { id: string }> {
  items: TItem[];
  isMobile: boolean;
  /** Query param used for desktop aside selection (default `id`). */
  previewParamKey?: string;
  /** Full-page route when `isMobile` is true. */
  mobileDetailPath: (item: TItem) => string;
  /** Builds aside preview payload when an item is selected on desktop. */
  buildPreview: (item: TItem) => DetailPreview;
  /**
   * When `false`, the hook neither opens nor closes the aside and skips URL
   * sync — used to hand the aside to another owner (e.g. a desktop creation
   * form) without this hook's effect closing it. Defaults to `true`.
   */
  enabled?: boolean;
}

/**
 * Shared master–detail contract for list pages (Goals, Vault, etc.).
 * Syncs `?id=` with preview state and wires DetailPreviewContext on desktop.
 */
export function useListPageWithPreview<TItem extends { id: string }>({
  items,
  isMobile,
  previewParamKey = 'id',
  mobileDetailPath,
  buildPreview,
  enabled = true,
}: UseListPageWithPreviewOptions<TItem>) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openPreview, closePreview } = useDetailPreview();
  const [previewId, setPreviewId] = useState<string | null>(null);

  const previewItem = useMemo(
    () => (previewId ? (items.find((item) => item.id === previewId) ?? null) : null),
    [items, previewId]
  );

  // Preserve any unrelated query params (filters etc.) — only touch our key.
  const setPreviewParam = useCallback(
    (value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null) next.delete(previewParamKey);
          else next.set(previewParamKey, value);
          return next;
        },
        { replace: true }
      );
    },
    [previewParamKey, setSearchParams]
  );

  const openItem = useCallback(
    (item: TItem) => {
      if (isMobile) {
        navigate(mobileDetailPath(item));
        return;
      }
      if (previewId === item.id) {
        setPreviewId(null);
        setPreviewParam(null);
        return;
      }
      setPreviewId(item.id);
      setPreviewParam(item.id);
    },
    [isMobile, mobileDetailPath, navigate, previewId, setPreviewParam]
  );

  const clearPreview = useCallback(() => {
    setPreviewId(null);
    setPreviewParam(null);
  }, [setPreviewParam]);

  useEffect(() => {
    if (!enabled) return;
    const idFromUrl = searchParams.get(previewParamKey);
    if (isMobile && idFromUrl) {
      const item = items.find((i) => i.id === idFromUrl);
      if (item) {
        navigate(mobileDetailPath(item), { replace: true });
      }
      return;
    }
    if (idFromUrl && items.length > 0) {
      const item = items.find((i) => i.id === idFromUrl);
      if (item && previewId !== item.id) {
        setPreviewId(item.id);
      }
    } else if (!idFromUrl && previewId) {
      setPreviewId(null);
    }
  }, [
    searchParams,
    items,
    previewId,
    isMobile,
    navigate,
    mobileDetailPath,
    previewParamKey,
    enabled,
  ]);

  useEffect(() => {
    if (!enabled) return;
    if (isMobile) {
      closePreview();
      return;
    }
    if (previewItem) {
      const preview = buildPreview(previewItem);
      openPreview({
        ...preview,
        onClose: () => {
          preview.onClose?.();
          clearPreview();
        },
      });
    } else {
      closePreview();
    }
  }, [previewItem, isMobile, openPreview, closePreview, buildPreview, clearPreview, enabled]);

  return {
    previewId,
    previewItem,
    selectedId: previewId,
    openItem,
    clearPreview,
  };
}
